import { FileDownload } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- client-side report, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import DataGridToolbar, { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { compareAlphanum } from '@sophocles/utils';

import { exportReportToExcel } from '../utils/reportExport';

import SolarDateSelector from './SolarDateSelector';

const round2 = v => _.round(v || 0, 2).toFixed(2);

/**
 * Shared grid for all solar reports (daily/weekly/monthly/yearly).
 * The variants only differ in the fetch action and the date period.
 */
function SolarReportGrid({
  period,
  initialDate,
  fetchAction,
  fileNamePrefix,
  title,
  baseStructureLink,
  isBillboard,
  siteGroupId,
  siteGroup,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const relayLoadTerm = useRelayLoadTerm();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endDate, setEndDate] = useState(initialDate);
  const [totals, setTotals] = useState({ consumed: '0.00', generated: '0.00', net: '0.00' });

  useEffect(() => {
    if (!siteGroupId || !endDate) return;
    setLoading(true);
    dispatch(fetchAction(siteGroupId, endDate))
      .then(res => {
        if (!res || res.error) {
          setRows([]);
          setTotals({ consumed: '0.00', generated: '0.00', net: '0.00' });
          return;
        }
        const totalsRaw = res.reduce(
          (acc, row) => ({
            consumed: acc.consumed + (row.get('consumed') || 0),
            generated: acc.generated + (row.get('generated') || 0),
          }),
          { consumed: 0, generated: 0 },
        );
        const nextTotals = {
          consumed: round2(totalsRaw.consumed),
          generated: round2(totalsRaw.generated),
          net: round2(totalsRaw.generated - totalsRaw.consumed),
        };
        const next = res
          .map(row => ({
            id: row.get('id', 0),
            site_id: row.get('site'),
            site_name: row.get('site_name', ''),
            load_names: row.get('load_names', ''),
            consumed: round2(row.get('consumed')),
            generated: round2(row.get('generated')),
            net: round2((row.get('generated') || 0) - (row.get('consumed') || 0)),
          }))
          .sort((a, b) => compareAlphanum(a.site_name, b.site_name))
          .toArray();
        setRows(next);
        setTotals(nextTotals);
      })
      .finally(() => setLoading(false));
  }, [dispatch, fetchAction, siteGroupId, endDate]);

  const columns = useMemo(
    () => [
      {
        field: 'site_name',
        headerName: isBillboard
          ? t('reports.structure', { defaultValue: 'Structure' })
          : t('reports.site', { defaultValue: 'Site' }),
        flex: 1,
        minWidth: 180,
        renderCell: params =>
          params.row.site_id ? (
            <Link to={`${baseStructureLink}${params.row.site_id}`}>{params.value}</Link>
          ) : (
            params.value
          ),
      },
      {
        field: 'load_names',
        headerName: relayLoadTerm.plural,
        flex: 1,
        minWidth: 140,
      },
      { field: 'consumed', headerName: t('reports.kwhConsumed', { defaultValue: 'Kwh Consumed' }), width: 150 },
      { field: 'generated', headerName: t('reports.kwhGenerated', { defaultValue: 'Kwh Generated' }), width: 150 },
      { field: 'net', headerName: t('reports.kwhNet', { defaultValue: 'Kwh Net' }), width: 130 },
    ],
    [t, isBillboard, baseStructureLink, relayLoadTerm],
  );

  const handleExport = useCallback(() => {
    exportReportToExcel({
      title,
      fileName: fileNamePrefix,
      rows: [
        ...rows,
        {
          site_name: t('reports.totalsRow', { defaultValue: 'TOTALS: {{count}}', count: rows.length }),
          load_names: '',
          consumed: totals.consumed,
          generated: totals.generated,
          net: totals.net,
        },
      ],
      columns,
      siteGroup,
      isBillboard,
      subHeaderRows: [[t('reports.periodEnding', { defaultValue: 'Period ending: {{date}}', date: endDate })]],
    });
  }, [title, fileNamePrefix, rows, totals, columns, siteGroup, isBillboard, endDate, t]);

  const toolbarContent = useMemo(
    () => <SolarDateSelector period={period} value={endDate} onChange={setEndDate} disabled={loading} />,
    [period, endDate, loading],
  );

  const exportButton = useMemo(
    () => (
      <ToolbarIconButton
        tooltip={t('general.export', { defaultValue: 'Export' })}
        onClick={handleExport}
        disabled={loading || rows.length === 0}
      >
        {loading ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
      </ToolbarIconButton>
    ),
    [t, handleExport, loading, rows.length],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar
        apiRef={apiRef}
        toolbarContent={toolbarContent}
        toolbarActions={exportButton}
        quickFilterPlaceholder={t('general.search')}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default SolarReportGrid;
